import clsx from 'clsx'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { trackClickEvent } from 'src/analytics/gtm'

import IdeaOfTheMonthLikeButton from './IdeaOfTheMonthLikeButton'
import ThumbnailSvg from '../../assets/homepage/ThumbnailSvg.png'
import { useHighlightedIdeas } from '../../hooks/useHighlightedIdeas'
import type { IdeaPreview } from '../../model/IdeaPreview'
import { useToggleIdeaLikeMutation } from '../../queries/ideasBank'
import { useIdeasBankStore } from '../../store/useIdeasBankStore'
import ImageWithFallback from '../ImageWithFallback/ImageWithFallback'

type WidgetMode = 'light' | 'dark'

interface IdeaOfTheMonthWidgetProps {
  mode?: WidgetMode
  className?: string
}

const DEFAULT_LABEL = 'Idea of the month'

const getSelectedIdea = (ideas: IdeaPreview[], selectedIdeaId?: string): IdeaPreview | undefined => {
  if (selectedIdeaId) {
    return ideas.find(idea => idea.id === selectedIdeaId) ?? ideas[0]
  }

  return ideas[0]
}

const IdeaOfTheMonthWidget = ({ mode = 'light', className }: IdeaOfTheMonthWidgetProps) => {
  const navigate = useNavigate()
  const [isHovered, setIsHovered] = useState(false)
  const [isPressed, setIsPressed] = useState(false)

  const highlightedIdeasQuery = useHighlightedIdeas({ page: 1, pageSize: 12 })
  const ideas = useMemo(() => highlightedIdeasQuery.data ?? [], [highlightedIdeasQuery.data])

  const upsertIdeas = useIdeasBankStore(state => state.upsertIdeas)
  const selectedIdeaId = useIdeasBankStore(state => state.selectedIdeaId)
  const setSelectedIdeaId = useIdeasBankStore(state => state.setSelectedIdeaId)
  const getLikeStateById = useIdeasBankStore(state => state.getLikeStateById)
  const toggleIdeaLike = useIdeasBankStore(state => state.toggleIdeaLike)
  const toggleIdeaLikeMutation = useToggleIdeaLikeMutation()

  useEffect(() => {
    if (ideas.length > 0) {
      upsertIdeas(ideas)
    }
  }, [ideas, upsertIdeas])

  useEffect(() => {
    if (!selectedIdeaId && ideas[0]) {
      setSelectedIdeaId(ideas[0].id)
    }
  }, [ideas, selectedIdeaId, setSelectedIdeaId])

  const selectedIdea = useMemo(() => getSelectedIdea(ideas, selectedIdeaId), [ideas, selectedIdeaId])

  const likeState = selectedIdea ? getLikeStateById(selectedIdea.id) : undefined
  const isLiked = Boolean(likeState?.isLikedByCurrentUser ?? selectedIdea?.isLikedByCurrentUser)

  const overlayVisible = isHovered || isPressed
  const pushClick = (payload: Parameters<typeof trackClickEvent>[0]): void => {
    trackClickEvent(payload)
  }

  const handleWidgetClick = () => {
    if (selectedIdea?.id) {
      pushClick({
        module: 'hero',
        module_title: 'idea of the month',
        click_text: `${selectedIdea.name}`,
        click_url: `/ideas-bank/idea/${selectedIdea.id}`,
        click_element: 'card',
      })
      navigate(`/ideas-bank/idea/${selectedIdea.id}`)
    }
  }

  const handleLikeClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()

    if (!selectedIdea?.id) {
      return
    }

    // Optimistic update (Zustand), then reconcile with server response (TanStack Query)
    toggleIdeaLike(selectedIdea.id)

    void toggleIdeaLikeMutation
      .mutateAsync({
        ideaId: selectedIdea.id,
        currentlyLiked: Boolean(likeState?.isLikedByCurrentUser ?? selectedIdea.isLikedByCurrentUser),
      })
      .then(serverResult => {
        // sync Zustand to server result
        useIdeasBankStore.getState().setLikeState(serverResult)
      })
      .catch(() => {
        // revert is handled minimally by Zustand pending flags in this phase
        // (future phase: implement proper rollback)
      })
  }

  if (!highlightedIdeasQuery.isLoading && !selectedIdea) {
    return null
  }
  if (ideas.length > 0) {
    return (
      <article
        className={clsx(
          'relative h-[214px] w-[214px] overflow-hidden rounded-[22px] border-2 bg-neutral-900 shadow-[0_12px_32px_rgba(0,0,0,0.18)]',
          mode === 'dark' ? 'border-[color:var(--color-neutral-90)]' : 'border-[color:var(--color-neutral-10)]',
          !highlightedIdeasQuery.isLoading && 'cursor-pointer',
          className,
        )}
        onClick={handleWidgetClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => {
          setIsHovered(false)
          setIsPressed(false)
        }}
        onMouseDown={() => setIsPressed(true)}
        onMouseUp={() => setIsPressed(false)}
      >
        {selectedIdea?.coverImageUrl ? (
          <ImageWithFallback
            src={selectedIdea.coverImageUrl}
            fallbackSrc={ThumbnailSvg}
            alt={selectedIdea.name}
            className="absolute inset-0 h-full w-full object-cover object-center"
            loading="lazy"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-[#f26522] to-[#1d1d1d]" />
        )}

        <div
          className={clsx(
            'absolute inset-0 bg-black transition-opacity duration-100 ease-out',
            overlayVisible ? 'opacity-50' : 'opacity-0',
          )}
        />

        <div className="absolute inset-0 flex flex-col justify-between">
          <div className="flex items-start justify-between p-2">
            <IdeaOfTheMonthLikeButton
              liked={isLiked}
              mode={mode}
              disabled={!selectedIdea || highlightedIdeasQuery.isLoading}
              onClick={handleLikeClick}
            />
          </div>

          <div className="space-y-1 text-white flex flex-col items-start text-left p-4">
            <p className="subtitle-xs text-[rgba(255, 255, 255, 0.7)] opacity-70 ">{DEFAULT_LABEL}</p>
            <p className="line-clamp-2 max-w-[160px] subtitle-l color-neutral-10">
              {selectedIdea?.name ?? 'Loading...'}
            </p>
          </div>
        </div>
      </article>
    )
  }
}

export default IdeaOfTheMonthWidget
